import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import { apiUnsubscribeReminder } from "@/lib/api";

/**
 * Public landing page for the one-click unsubscribe link embedded in
 * every weekly Sunday-digest email. The token is the only auth — we
 * deliberately don't ask the user to sign in here so the unsubscribe
 * is friction-free (an unsubscribe flow that demands a login is the
 * fastest way to land on Resend's spam-complaint list).
 */
export default function Unsubscribe() {
    const { token } = useParams();
    const [status, setStatus] = useState("loading");  // loading | done | error
    const [email, setEmail] = useState(null);
    const [alreadyOff, setAlreadyOff] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await apiUnsubscribeReminder(token);
                if (cancelled) return;
                setEmail(res.email || null);
                setAlreadyOff(!!res.already_unsubscribed);
                setStatus("done");
            } catch (err) {
                if (!cancelled) setStatus("error");
            }
        })();
        return () => { cancelled = true; };
    }, [token]);

    return (
        <div className="min-h-screen flex items-center justify-center px-6 py-16 paper-texture" data-testid="unsubscribe-page">
            <div className="w-full max-w-md">
                <div className="flex items-center gap-3 mb-8 justify-center">
                    <Logo variant="icon" size={56} />
                    <div>
                        <div className="font-serif-display text-3xl text-[var(--vm-text)] leading-none lowercase tracking-tight">rooteed</div>
                        <div className="text-xs uppercase tracking-[0.2em] text-[var(--vm-sage)] mt-1">Garden Journal</div>
                    </div>
                </div>

                <div className="journal-card p-8 text-center" data-testid="unsubscribe-card">
                    {status === "loading" && (
                        <div data-testid="unsubscribe-loading">
                            <Loader2 className="w-6 h-6 mx-auto text-[var(--vm-sage)] animate-spin" />
                            <p className="text-sm text-[var(--vm-text-muted)] mt-3">
                                Updating your preferences…
                            </p>
                        </div>
                    )}

                    {status === "error" && (
                        <div data-testid="unsubscribe-error">
                            <div className="w-12 h-12 rounded-full bg-[var(--vm-secondary)]/15 flex items-center justify-center mx-auto mb-4">
                                <AlertCircle className="w-6 h-6 text-[var(--vm-secondary)]" strokeWidth={1.6} />
                            </div>
                            <h1 className="font-serif-display text-2xl text-[var(--vm-text)] mb-2">
                                Something went wrong
                            </h1>
                            <p className="text-sm text-[var(--vm-text-secondary)]">
                                We couldn't update your preferences right now. You can also turn the digest off from your Profile.
                            </p>
                            <Link to="/profile" className="block mt-5">
                                <Button data-testid="unsubscribe-go-profile"
                                    className="rounded-full bg-[var(--vm-accent)] hover:bg-[var(--vm-accent-hover)] text-[var(--vm-bg)]">
                                    Go to Profile
                                </Button>
                            </Link>
                        </div>
                    )}

                    {status === "done" && (
                        <div data-testid="unsubscribe-done">
                            <div className="w-12 h-12 rounded-full bg-[var(--vm-sage)]/15 flex items-center justify-center mx-auto mb-4">
                                <CheckCircle2 className="w-6 h-6 text-[var(--vm-sage)]" strokeWidth={1.6} />
                            </div>
                            <h1 className="font-serif-display text-2xl text-[var(--vm-text)] mb-2">
                                {alreadyOff ? "You were already unsubscribed" : "You're unsubscribed"}
                            </h1>
                            <p className="text-sm text-[var(--vm-text-secondary)]">
                                {email
                                    ? <>No more Sunday digests for <strong>{email}</strong>.</>
                                    : "We've turned the Sunday digest off for that account."}
                                <br />You can turn it back on anytime from your Profile.
                            </p>
                            <Link to="/" className="block mt-5">
                                <Button data-testid="unsubscribe-back-home"
                                    variant="outline"
                                    className="rounded-full border-[var(--vm-border)]">
                                    Back to rooteed
                                </Button>
                            </Link>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
